import { Injectable, inject } from '@angular/core';

import {
    DeviceService,
    HostApiService,
    LoadingIndicatorHandler,
    LoadingIndicatorService,
    Logger,
    NativeAppService,
    TimerService,
    TrackingService,
    UserService,
} from '@frontend/vanilla/core';
import { once } from 'lodash-es';
import { Observable, firstValueFrom } from 'rxjs';

import { GeoCoordinatorActivatorService } from './activator.service';
import { GeoCoordinatorClientConfig } from './client-config';
import { CoordinatorStates, GDSResponse, IErrorData, Optional } from './client-models/coordinator.models';
import { LocationInfoStatus } from './client-models/location-info';
import { RetryTypes, eventConstants } from './constants';
import { DeviceInfoResponse } from './device-info/device-info.response';
import { GeoCoordinationOverlayService } from './geo-coordination-overlay.service';
import { GeoCoordinatorService } from './geo-coordinator.service';
import { PermissionInfo } from './permissions-manager/permissions';
import { GeoCoordinatorPermissionsManagerService } from './permissions-manager/permissions-manager.service';

const loadEntainAppInstallerComponent = () =>
    import(/* webpackChunkName: "vn-geo-coordination-download-entain-app" */ './download-native-app/download-entain-app.component').then(
        (m) => m.DownloadEntainAppComponent,
    );
const loadMobileLaunchWidgetComponent = () =>
    import(
        /* webpackChunkName: "vn-geo-coordination-mobile-launch-widget" */ './launch-widgets/mobile-launch-widget/mobile-launch-widget.component'
    ).then((m) => m.GeoCoordinationMobileLaunchWidgetComponent);
const loadGeoCoordinationErrorComponent = () =>
    import(/* webpackChunkName: "vn-geo-coordination-error" */ './geo-coordination-error/geo-coordination-error.component').then(
        (m) => m.GeoCoordinationErrorComponent,
    );
const loadDesktopLaunchWidgetComponent = () =>
    import(
        /* webpackChunkName: "vn-geo-coordination-desktop-launch-widget" */ './launch-widgets/desktop-launch-widget/desktop-launch-widget.component'
    ).then((m) => m.GeoCoordinationDesktopLaunchWidgetComponent);

@Injectable({
    providedIn: 'root',
})
export class GeoCoordinationCheckService {
    private readonly activator = inject(GeoCoordinatorActivatorService);
    private readonly geolocationCoordinatorService = inject(GeoCoordinatorService);
    private readonly deviceService = inject(DeviceService);
    private readonly nativeAppService = inject(NativeAppService);
    private readonly geolocationConfig = inject(GeoCoordinatorClientConfig);
    private readonly user = inject(UserService);
    private readonly loadingIndicator = inject(LoadingIndicatorService);
    private readonly timerService = inject(TimerService);
    private readonly trackingService = inject(TrackingService);
    private readonly logger = inject(Logger);
    private readonly geoCoordinatorPermissionsManagerService = inject(GeoCoordinatorPermissionsManagerService);
    private readonly geolocationOverlayService = inject(GeoCoordinationOverlayService);
    private readonly apiServiceExternal = inject(HostApiService);

    private isLocationFetched = false;
    private loading: LoadingIndicatorHandler | null;
    private isLocationFetchTimeOut = false;
    private resetTimer: Optional<NodeJS.Timeout> = null;
    private gdsApiInterval = 1000; //this.geolocationConfig.locationInfoStatus.retry.initialInterval;
    private resetTimerAndRefetchLocation = false;
    private errorResponse: GDSResponse;
    private deviceInfoResponse: DeviceInfoResponse = {};

    /**
     * Check geolocation restriction and calls the `callback` function at most once.
     * This function uses a callback instead of something like Observables because it's used in many places (like the pick component) and we don't want to introduce an overhead.
     *
     * @param [onContinue]
     * @param [options]
     * @returns
     * @memberof GeoCoordinationCheckService
     */
    // eslint-disable-next-line max-lines-per-function
    async checkGeolocation(
        onContinue?: (isLocationFailed: boolean) => void,
        options?: { readonly waitForLocationAvailable?: boolean },
    ): Promise<void> {
        const callback = once(onContinue || (() => {}));

        await firstValueFrom(this.geolocationConfig.whenReady);

        if (await this.activator.isActive()) {
            this.gdsApiInterval = this.geolocationConfig.locationInfoStatus.retry.initialInterval;
            if (this.nativeAppService.isNative && options?.waitForLocationAvailable) {
                // If it's native, this is the only thing we do, given the product context commanded it via waitForLocationAvailable
                this.validateLocation(callback);
            } else {
                // we are processing web-context here
                if (this.shouldDownloadAppInstead()) {
                    return;
                }

                if (this.nativeAppService.isNative) {
                    callback(false);
                    return;
                }

                if (!this.loading && !this.isLocationFetched) {
                    this.loading = this.loadingIndicator.start();
                }

                this.geolocationCoordinatorService.onInitialized = (
                    initializationStatus: Optional<CoordinatorStates>,
                    locationInfoStatus?: LocationInfoStatus,
                ) => {
                    this.logger.info(
                        '[GeoCoordinationCheckService]',
                        '[checkGeolocation]',
                        '[geolocationCoordinatorService:onInitialized]',
                        initializationStatus,
                    );
                    if (this.geolocationConfig.enableNDBS) {
                        if (initializationStatus === CoordinatorStates.INITIALIZED) {
                            this.stopLoader();
                            this.geolocationCoordinatorService.handleLocationInfoStatus(
                                locationInfoStatus,
                                (permissionsInfo: PermissionInfo) => {
                                    this.geoCoordinatorPermissionsManagerService.handlePermissionsInfo({
                                        permissionsInfo,
                                        geoCoordinatorService: this.geolocationCoordinatorService,
                                        textDictionary: {},
                                    });
                                },
                                () => {
                                    this.geoCoordinatorPermissionsManagerService.closePermissionsInfo();
                                    callback(false);
                                },
                            );
                        }
                    } else {
                        if (initializationStatus === CoordinatorStates.INITIALIZED) {
                            this.stopLoader();

                            if (options?.waitForLocationAvailable) {
                                this.validateLocation(callback);
                            } else {
                                callback(false);
                            }
                        }
                    }
                };

                this.geolocationCoordinatorService.onUninitialized = () => {
                    this.logger.info('[GeoCoordinationCheckService]', '[geolocationCoordinatorService:onUninitialized]');
                    if (!this.geolocationConfig.enableNDBS) {
                        callback(true);
                        this.displayLaunchWidgetOnUninitialize();
                    } else {
                        const persistedLocationInfo =
                            this.geolocationCoordinatorService.readLocationInfoStatus && this.geolocationCoordinatorService.readLocationInfoStatus();

                        if (persistedLocationInfo) {
                            this.geolocationCoordinatorService.handleLocationInfoStatus(
                                persistedLocationInfo,
                                (permissionsInfo: PermissionInfo) => {
                                    this.geoCoordinatorPermissionsManagerService.handlePermissionsInfo({
                                        permissionsInfo,
                                        textDictionary: {} as { [key: string]: string },
                                        geoCoordinatorService: this.geolocationCoordinatorService,
                                    });
                                },
                                () => this.geoCoordinatorPermissionsManagerService.closePermissionsInfo(),
                            );
                        }
                    }

                    this.stopLoader();
                };

                this.geolocationCoordinatorService.onError = (errorData: IErrorData) => {
                    this.logger.error('[GeoCoordinationCheckService]', '[geolocationCoordinatorService:onError]', errorData);
                    this.stopLoader();
                    this.showLaunchWidgetOnError(errorData, callback);
                };

                //NDBS stands for "No Download Browser Solution" and is one of the Geolocation fetch solutions provided by GeoComply
                //which elimnates the need to install the geolocation downloader when enabled
                if (!this.geolocationConfig.enableNDBS) {
                    this.geolocationCoordinatorService.initialize({ showPLCPopup: false });
                    return;
                }

                const permissionsManager = this.geolocationCoordinatorService?.permissionsManager;
                if (!permissionsManager) {
                    this.stopLoader();
                    callback(false);
                    return;
                }

                permissionsManager.onPermissionInfo = (permissionsInfo: PermissionInfo) => {
                    if (permissionsInfo.status === 'available') {
                        this.geoCoordinatorPermissionsManagerService.closePermissionsInfo();
                        this.geolocationCoordinatorService.initialize({ showPLCPopup: false });
                        this.stopLoader();
                        return;
                    }

                    if (permissionsInfo.status === 'notavailable') {
                        this.geoCoordinatorPermissionsManagerService.handlePermissionsInfo({
                            permissionsInfo,
                            textDictionary: {},
                            geoCoordinatorService: this.geolocationCoordinatorService,
                        });
                        this.stopLoader();
                    }
                };

                permissionsManager.seekPermission();
            }
        } else {
            callback(false);
        }
    }

    checkGeoLocation$(options: { readonly waitForLocationAvailable: boolean }): Observable<boolean> {
        return new Observable((subscriber) => {
            this.checkGeolocation((isLocationFailed) => {
                subscriber.next(isLocationFailed);
                subscriber.complete();
            }, options);
        });
    }

    registerUnload() {
        this.geolocationCoordinatorService.registerUnload();
    }

    registerSynchronization() {
        this.geolocationCoordinatorService.registerSynchronization();
    }

    private stopLoader(): void {
        this.loading?.done();
        this.loading = null;
    }

    private validateLocation(callback: (_: boolean) => void): void {
        this.isLocationFetchTimeOut = false;
        this.isLocationFetched = false;
        this.resetTimerAndRefetchLocation = false;
        this.errorResponse = {
            locationStatus: '',
            isLocationAvailable: false,
            errorCode: 0,
            errorMsg: '',
        } satisfies GDSResponse;
        this.gdsApiInterval = this.geolocationConfig.locationInfoStatus.retry.initialInterval;
        this.getLocationDetailsfromGDS(callback);
        this.startTimer(callback);
    }

    private startTimer(callback: (_: boolean) => void): void {
        this.clearTimer();

        this.resetTimer = this.timerService.setTimeout(() => {
            this.isLocationFetchTimeOut = true;
            if (!this.isLocationFetched) {
                if (this.geolocationConfig.errorSettings.logFailures) {
                    this.logGeoLocationError(new Date(), this.errorResponse);
                }
                this.showGeoLocationError();
                callback(true);
            }
        }, this.gdsApiInterval);
    }

    private getLocationDetailsfromGDS(callback: (_: boolean) => void): void {
        if (this.isLocationFetchTimeOut || !this.geolocationConfig.locationInfoStatus.fetchUrl) {
            return;
        }
        const startGDSDate = new Date();
        let payload = `ssoKey=${this.user.ssoToken}`;

        if (this.geolocationConfig.enableNDBS) {
            payload += '&licenseSolution=ndbs';
        }

        this.apiServiceExternal.post(this.geolocationConfig.locationInfoStatus.fetchUrl, payload).subscribe({
            next: (result) => this.processGDSResponse(result, callback),
            error: (error: unknown) => this.logGeoLocationError(startGDSDate, error),
            complete: () => console.info('GDS request completed'),
        });
    }

    private processGDSResponse(result: GDSResponse, callback: (_: boolean) => void): void {
        if (!result?.isLocationAvailable) {
            this.handleUnavailableLocation(result, callback);
            return;
        }

        if (result.locationStatus === 'VALID_LOCATION' && result.errorCode === 0) {
            this.handleValidLocation(result, callback);
        } else {
            this.handleInvalidLocation(result, callback);
        }
    }

    private handleValidLocation(result: GDSResponse, callback: (_: boolean) => void): void {
        this.isLocationFetched = true;
        this.deviceInfoResponse = this.getDeviceResponse(result);
        callback(false);
    }

    private handleInvalidLocation(result: GDSResponse, callback: (_: boolean) => void): void {
        this.errorResponse = result;
        if (this.isRetryableError(result)) {
            this.isLocationFetched = false;
            this.refetchLocationOnRetryableErrorCodes(callback);
            this.refetchGdsApi(callback);
        } else {
            this.clearTimer();
            this.showGeoLocationError();
            callback(true);
        }
    }

    private handleUnavailableLocation(result: GDSResponse, callback: (_: boolean) => void): void {
        this.errorResponse = result;
        this.refetchGdsApi(callback);
    }

    private isRetryableError(result: GDSResponse): boolean {
        return (
            this.geolocationConfig.locationInfoStatus.retry.enabled &&
            !!result?.troubleShooterMessages?.tsMsgs?.some((errMsg) => errMsg.retry === RetryTypes.Retry)
        );
    }

    private refetchLocationOnRetryableErrorCodes(callback: (_: boolean) => void): void {
        if (!this.resetTimerAndRefetchLocation) {
            this.isLocationFetchTimeOut = false;
            this.gdsApiInterval = this.geolocationConfig.locationInfoStatus.retry.interval;
            this.resetTimerAndRefetchLocation = true;
            this.startTimer(callback);
        }
    }

    private clearTimer(): void {
        if (this.resetTimer) {
            this.timerService.clearTimeout(this.resetTimer);
        }
    }

    private refetchGdsApi(callback: (_: boolean) => void): void {
        this.timerService.setTimeout(() => {
            this.getLocationDetailsfromGDS(callback);
        }, 1000);
    }

    async showGeoLocationError(): Promise<void> {
        const componentToLoad = await loadGeoCoordinationErrorComponent();
        const errorMessages = this.errorResponse?.troubleShooterMessages?.tsMsgs ?? [];

        this.geolocationOverlayService.show(componentToLoad, {
            cssClasses: ['geo-comply-error-dialog'],
            errorMessages,
        });

        this.geolocationCoordinatorService.onError = undefined;
        this.geolocationCoordinatorService.onInitialized = undefined;
        this.doTracking();
    }

    private displayLaunchWidgetOnUninitialize() {
        if (this.geolocationConfig.interactiveLaunch.displayWidgetOnUninitialize) {
            this.displayLaunchWidget();
        }
    }

    private showLaunchWidgetOnError(errorData: IErrorData, callback: (_: boolean) => void) {
        if (this.geolocationConfig.enableNDBS) {
            callback(false);
        } else {
            if (
                this.geolocationConfig.interactiveLaunch.displayWidgetOnError ||
                this.geolocationConfig.interactiveLaunch.displayWidgetOnErrorCodes.includes(errorData && errorData.errorCode)
            ) {
                this.displayLaunchWidget(errorData);
            } else {
                callback(false);
            }
        }
    }

    private displayLaunchWidget(errorData: IErrorData | undefined = undefined) {
        if (this.deviceService.isMobile) {
            this.displayMobileLaunchWidget(errorData);
        } else {
            this.displayDesktopLaunchWidget();
        }
    }

    private async displayDesktopLaunchWidget(): Promise<void> {
        const componentToLoad = await loadDesktopLaunchWidgetComponent();
        this.geolocationOverlayService.show(componentToLoad, { cssClasses: ['geo-location-popup'] });
        this.geolocationCoordinatorService.onError = undefined;
        this.geolocationCoordinatorService.onInitialized = undefined;
    }

    private async displayMobileLaunchWidget(errorData: IErrorData | undefined = undefined): Promise<void> {
        const componentToLoad = await loadMobileLaunchWidgetComponent();
        this.geolocationOverlayService.show(componentToLoad, { cssClasses: ['geo-guard-validation'], errorData });
        this.geolocationCoordinatorService.onError = undefined;
        this.geolocationCoordinatorService.onInitialized = undefined;
    }

    private async showDownloadAppDialog(): Promise<void> {
        const componentToLoad = await loadEntainAppInstallerComponent();
        this.geolocationOverlayService.show(componentToLoad, { cssClasses: ['native-app-installer'] });
    }

    private shouldDownloadAppInstead(): boolean {
        if (
            this.geolocationConfig.nativeSettings.continueOnApps &&
            !this.nativeAppService.isNative &&
            this.user.isAuthenticated &&
            !this.geolocationConfig.enableNDBS
        ) {
            this.showDownloadAppDialog();

            return true;
        }
        return false;
    }

    private doTracking(): void {
        this.trackingService.triggerEvent(eventConstants.EVENT_GEOLOCATION_ERROR, {
            [eventConstants.GEOLOCATION_ERRORCODE]: this.errorResponse?.errorCode,
            [eventConstants.GEOLOCATION_ERRORMESSAGE]: this.errorResponse?.errorMsg,
        });
    }

    private logGeoLocationError(startGDSDate: Date, errorMessage: GDSResponse | any): void {
        if (this.geolocationConfig.errorSettings.logFailures) {
            this.logger.error(
                `GDS api url: ${this.geolocationConfig.locationInfoStatus.fetchUrl}, ran for: ${Date.now() - startGDSDate.valueOf()}ms, error: ${JSON.stringify(errorMessage)}, ssotoken: ${this.user.ssoToken?.slice(0, 100)}`,
            );
        }
    }

    private getDeviceResponse(response: GDSResponse): DeviceInfoResponse {
        return {
            deviceModelOS: response.deviceModelOS,
            deviceModelSolution: response.deviceModelSolution,
            deviceUUid: response.deviceUUid,
            regionCode: response.regionCode,
            regionName: response.regionName,
        };
    }

    getDeviceInfoResponse(): DeviceInfoResponse {
        return this.deviceInfoResponse;
    }
}
